/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class FacilityService {
  constructor(public readonly httpRequest: BaseHttpRequest) {}

  /**
   * Update Facility Med Window Time **CUSTOMER_CARE, ENGINEERING or FACILITY_ADMIN**
   * @param facilityId
   * @param requestBody
   * @returns any Success
   * @throws ApiError
   */
  public updateFacility(
    facilityId?: number,
    requestBody?: {
      med_window_time_sec?: number;
    },
  ): CancelablePromise<any> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/facility/{facility_id}',
      path: {
        facility_id: facilityId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `BAD_REQUEST`,
        401: `UNAUTHORIZED`,
        403: `FORBIDDEN`,
        404: `NOT_FOUND`,
        500: `INTERNAL_SERVER_ERROR`,
        503: `SERVICE_UNAVAILABLE`,
      },
    });
  }

  /**
   * Get Facility info by ID
   * @param facilityId Facility ID
   * @returns any Successful
   * @throws ApiError
   */
  public getFacilityById(facilityId?: number): CancelablePromise<{
    id?: number;
    name?: string;
    med_window_time_sec?: number;
    created_at?: string;
  }> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/facility/{facility_id}/info',
      path: {
        facility_id: facilityId,
      },
      errors: {
        403: `Forbidden`,
        500: `INTERNAL_SERVER_ERROR`,
      },
    });
  }

  /**
   * get list of Devices assigned to facility
   * @param facilityId Facility ID
   * @returns any Successfully get devices in a facility
   * @throws ApiError
   */
  public getFacilityDevices(facilityId?: number): CancelablePromise<
    Array<{
      id?: number;
      serial_number?: string;
      owner?: {
        id?: number;
        email?: string;
        first_name?: string;
        last_name?: string;
      } | null;
      synced_by?: {
        id?: number;
        email?: string;
        first_name?: string;
        last_name?: string;
      } | null;
      hardware_version?: string;
      software_version?: string;
      software_channel_id?: number;
      pinned_software_version_id?: number | null;
      pinned_software_version_name?: string | null;
    }>
  > {
    return this.httpRequest.request({
      method: 'GET',
      url: '/facility/{facility_id}/devices',
      path: {
        facility_id: facilityId,
      },
      errors: {
        403: `Forbidden`,
        500: `INTERNAL_SERVER_ERROR`,
      },
    });
  }

  /**
   * get list of Customers of facility
   * @param facilityId Facility ID
   * @returns any Successfully get customers in a facility
   * @throws ApiError
   */
  public getFacilityCustomers(facilityId?: number): CancelablePromise<
    Array<{
      customer_id?: number;
      email?: string;
      first_name?: string;
      last_name?: string;
      phone?: string;
      phone2?: string;
      address1?: string;
      address2?: string;
      city?: string;
      state?: string;
      zipcode?: string;
      country?: string;
      gender_id?: number;
      date_of_birth?: string;
      is_patient?: boolean;
      previlege_id?: number;
      med_window_time_sec?: number;
      last_login_at?: string;
      email_verified_at?: string;
      created_at?: string;
      modified_at?: string;
      facility_id?: number;
      facility_role_id?: number;
      device_time_zone?: string;
    }>
  > {
    return this.httpRequest.request({
      method: 'GET',
      url: '/facility/{facility_id}/customers',
      path: {
        facility_id: facilityId,
      },
      errors: {
        403: `Forbidden`,
        500: `INTERNAL_SERVER_ERROR`,
      },
    });
  }

  /**
   * MVP endpoint specifically for ecumen. Should be deleted after MVP
   * @param requestBody The customer payload
   * @returns any Successfully created user
   * @throws ApiError
   */
  public postFacilityCustomer(requestBody?: {
    first_name: string;
    last_name: string;
    username: string;
    facility_role_id: number;
    gender?: number;
    pharmacy?: number;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zipcode?: number;
    country?: string;
    date_of_birth?: string;
  }): CancelablePromise<any> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/facility/customer',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Invalid parameters`,
        401: `Unauthorized`,
        403: `Forbidden`,
        500: `Internal server error`,
      },
    });
  }

  /**
   * Return customer's personal information.
   * @param customerId customer ID
   * @returns any Successfully get customer information.
   * @throws ApiError
   */
  public getFacilityCustomerDetail(customerId?: number): CancelablePromise<{
    customer_id?: number;
    email?: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
    phone2?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zipcode?: string;
    country?: string;
    gender_id?: number;
    date_of_birth?: string;
    is_patient?: boolean;
    previlege_id?: number;
    med_window_time_sec?: number;
    last_login_at?: string;
    email_verified_at?: string;
    created_at?: string;
    modified_at?: string;
    facility_id?: number;
    facility_role_id?: number;
    device_time_zone?: string;
  }> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/facility/customer/{customer_id}',
      path: {
        customer_id: customerId,
      },
      errors: {
        400: `Invalid  parameters`,
        401: `UNAUTHORIZED`,
        403: `Forbidden`,
        404: `NOT_FOUND`,
        500: `INTERNAL_SERVER_ERROR`,
        503: `SERVICE_UNAVAILABLE`,
      },
    });
  }
  /**
   * Survey score insights for a location — per survey type distribution, trend, average, participation and each responder's lowest score over a rolling window ending today
   * @param facilityId
   * @param days Length of the rolling window ending today in time_zone (default 30, max 92)
   * @param granularity Trend bucket size; defaults to day when days is 31 or fewer, otherwise week
   * @param surveyTypeId Restrict to one survey type (1 = hub overall experience, 2 = daily wellness check)
   * @param timeZone IANA time zone the window and its days are cut on, normally the viewer's browser zone (e.g. America/Chicago). Defaults to Etc/UTC.
   * @returns any Success
   * @throws ApiError
   */
  public getFacilitySurveyInsights(
    facilityId: number,
    days?: number,
    granularity?: 'day' | 'week',
    surveyTypeId?: 1 | 2,
    timeZone?: string,
  ): CancelablePromise<{
    facility_id: number;
    /**
     * The resolved rolling window; dates are calendar days in time_zone, inclusive
     */
    period: {
      days: number;
      from: string;
      /**
       * Today in time_zone
       */
      to: string;
      granularity: 'day' | 'week';
      /**
       * IANA time zone the window was cut on (the request's time_zone, else Etc/UTC)
       */
      time_zone: string;
    };
    /**
     * Patients currently assigned to the location
     */
    patient_count: number;
    /**
     * One block per survey type (scales differ, so metrics are never blended across types)
     */
    surveys: Array<{
      /**
       * Survey type identifier (1 = hub overall experience, 2 = daily wellness check)
       */
      survey_type_id: number;
      survey_type: 'HUB_OVERALL_EXPERIENCE' | 'HUB_DAILY_WELLNESS_CHECK' | null;
      /**
       * Survey question as stored on the survey type
       */
      title: string;
      min_score: number;
      max_score: number;
      /**
       * Patient-days in the window with a response, counting only answers given while the patient was assigned to the location; when a patient answers several times on a day, only the latest answer on the survey type's scale is counted
       */
      response_count: number;
      /**
       * Mean of the counted patient-days over the window, rounded to 2 decimals; null when there are no responses
       */
      average_score: number | null;
      /**
       * Patients eligible in the window (assigned to the location at any point in it) who answered this survey type at least once versus those who did not
       */
      participation: {
        /**
         * Patients whose assignment to the location overlapped the window (the denominator)
         */
        patients_eligible: number;
        patients_responded: number;
        patients_not_responded: number;
        /**
         * patients_responded as a percentage of patients_eligible, rounded to 1 decimal; 0 when no patient was eligible
         */
        participation_rate: number;
      };
      /**
       * One entry per score on the type's scale, zeros included
       */
      distribution: Array<{
        score: number;
        label: string | null;
        count: number;
        /**
         * count as a percentage of response_count, rounded to 1 decimal
         */
        percentage: number;
      }>;
      /**
       * One entry per bucket covering the whole window; weekly buckets start on Monday and the first/last are clamped to the window
       */
      trend: Array<{
        bucket_start: string;
        bucket_end: string;
        response_count: number;
        /**
         * Distinct patients with a counted answer in the bucket
         */
        patients_responded: number;
        /**
         * null when the bucket has no responses
         */
        average_score: number | null;
      }>;
      /**
       * Every patient of the location who answered this survey type in the window, with their lowest counted score and the most recent day they gave it; worst first, then by patient name
       */
      lowest_scores: Array<{
        customer_id: number;
        first_name: string | null;
        last_name: string | null;
        score: number;
        label: string | null;
        /**
         * Most recent local day (in time_zone) the patient gave that score
         */
        day: string | null;
      }>;
    }>;
  }> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/facility/{facility_id}/survey_insights',
      path: {
        facility_id: facilityId,
      },
      query: {
        days: days,
        granularity: granularity,
        survey_type_id: surveyTypeId,
        time_zone: timeZone,
      },
      errors: {
        400: `BAD_REQUEST`,
        401: `UNAUTHORIZED`,
        403: `FORBIDDEN`,
        500: `INTERNAL_SERVER_ERROR`,
      },
    });
  }
}
